import { usePost } from "@albertine/api-client";
import { format } from "date-fns";
import { useState } from "react";
import type { Message } from "../../../common/types/Message";
import { NotificationDeliveryMethod } from "../../../common/types/NotificationDeliveryMethod";
import { EventEmitter } from "../../../EventEmitter";
import { formatMemberName } from "../../../utils/name.util";
import Button from "../../Common/Buttons";
import RoundedSelectionToggle from "../../Common/RoundedSelectionToggle";
import ChatMessageBubble from "../ChatMessageBubble";
import ChatMessageInner from "../ChatMessageInner";
import "./ResendNotificationView.css";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { isTypeOf } from "../../../common/utils/zod.util";
import { defaultDateTimeFormat } from "../../../utils/time.util";
import { fromTimestampToDate } from "../../../utils/timestamp.util";
import AlertNotification from "../../Common/AlertNotification";
import Icon from "../../Common/Icon";

type Props = {
    conversationId: string;
    currentMember: MemberDataFirestoreRecord;
    message: Message;
    close: (() => void) | undefined;
};

const ResendNotificationView = function ResendNotificationView(props: Props) {
    const { message, conversationId, currentMember, close } = props;

    const [deliveryMethod, setDeliveryMethod] =
        useState<NotificationDeliveryMethod>("push-notification");
    const { mutate: resendNotification, isPending: isSending } = usePost(
        "resendConversationMessageNotification",
        {
            onSuccess: () => {
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: `${formatMemberName(currentMember)} notified successfully`,
                        type: "info",
                    },
                });
                close?.();
            },
            onError: (error) => {
                window.alert(error.error ?? "Could not send the notification.");
            },
        },
    );

    const memberRenotifiedLastTime = message.additionalNotificationAttempts
        ? fromTimestampToDate(
              message.additionalNotificationAttempts[
                  message.additionalNotificationAttempts.length - 1
              ].sentAt,
          )
        : undefined;

    const askToResendNotification = () => {
        if (!currentMember || !conversationId) return;
        resendNotification({
            param: {
                conversationId,
                messageId: message.id,
            },
            json: { notificationMethod: deliveryMethod },
        });
    };

    return (
        <div className="resend-notification">
            <div>
                <h4>Notify member again?</h4>
                <p>Are you sure you want to notify the member again?</p>

                {message.additionalNotificationAttempts && memberRenotifiedLastTime && (
                    <AlertNotification>
                        Member has been already notified{" "}
                        {message.additionalNotificationAttempts.length}{" "}
                        {message.additionalNotificationAttempts.length <= 1
                            ? "time"
                            : "times"}
                        .<br />
                        Last time was at{" "}
                        {format(memberRenotifiedLastTime, defaultDateTimeFormat)}.
                    </AlertNotification>
                )}

                <div className="resend-notification__message-container">
                    <ChatMessageInner sender="agent" messageBase={message}>
                        <ChatMessageBubble
                            message={message}
                            sender="agent"
                            currentMember={currentMember}
                        />
                    </ChatMessageInner>
                </div>
                <div className="resend-notification__footer">
                    <RoundedSelectionToggle
                        initialValue={deliveryMethod}
                        onChange={(value) => {
                            if (isTypeOf(value, NotificationDeliveryMethod)) {
                                setDeliveryMethod(value);
                            }
                        }}
                        options={[
                            {
                                label: "In App",
                                value: NotificationDeliveryMethod.enum[
                                    "push-notification"
                                ],
                            },
                            {
                                label: "Email",
                                value: NotificationDeliveryMethod.enum.email,
                            },
                        ]}
                    />
                    <Button.Secondary
                        label="Notify member"
                        disabled={isSending}
                        showLoadingSpinner={isSending}
                        onClick={askToResendNotification}
                    >
                        <Icon.NotificationAdd.Small />
                    </Button.Secondary>
                </div>
            </div>
        </div>
    );
};

export default ResendNotificationView;
